import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { getNotifications, markAllRead } from '../utils/api';
import {
  HomeIcon, UsersIcon, PresentationChartBarIcon, CalendarDaysIcon,
  MegaphoneIcon, FolderOpenIcon, CheckCircleIcon, ChartBarIcon,
  ShieldCheckIcon, BellIcon, UserCircleIcon, Bars3Icon, XMarkIcon,
  ArrowRightOnRectangleIcon, Cog6ToothIcon, ChartPieIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const NAV_LINKS = [
  { to: '/admin-home', icon: ShieldCheckIcon, label: 'Admin Home', adminOnly: true },
  { to: '/dashboard', icon: HomeIcon, label: 'Dashboard', supervisorHide: true, adminHide: true },
  { to: '/supervisor', icon: HomeIcon, label: 'My Fellows', supervisorOnly: true },
  { to: '/fellows', icon: UsersIcon, label: 'Fellows' },
  { to: '/presentations', icon: PresentationChartBarIcon, label: 'Presentations' },
  { to: '/meetings', icon: CalendarDaysIcon, label: 'Meetings' },
  { to: '/announcements', icon: MegaphoneIcon, label: 'Announcements' },
  { to: '/resources', icon: FolderOpenIcon, label: 'Resources' },
  { to: '/tasks', icon: CheckCircleIcon, label: 'Tasks', supervisorHide: true },
  { to: '/calendar', icon: CalendarDaysIcon, label: 'Calendar' },
  { to: '/polls', icon: ChartPieIcon, label: 'Polls' },
];

const ADMIN_LINKS = [
  { to: '/analytics', icon: ChartBarIcon, label: 'Analytics' },
  { to: '/admin', icon: ShieldCheckIcon, label: 'Admin Panel' },
];

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const isSupervisor = user?.role === 'supervisor';
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const { data: notifData, refetch } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => getNotifications({ unread_only: true }),
    refetchInterval: 30000,
  });

  const unreadCount = notifData?.data?.length || 0;

  const handleMarkAllRead = async () => {
    await markAllRead();
    refetch();
    toast.success('Notifications cleared');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const Sidebar = () => (
    <aside className="flex flex-col w-64 bg-surface-50 border-r border-gray-800 h-full">
      {/* Logo */}
      <div className="px-5 py-5 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary-600 flex items-center justify-center text-white font-bold text-sm">T</div>
          <div>
            <div className="text-white font-bold text-sm leading-tight">THRU Zim</div>
            <div className="text-gray-500 text-xs">MSc Fellowship</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <div className="text-gray-600 text-xs font-semibold uppercase tracking-wider px-3 mb-2">Main</div>
        {NAV_LINKS.filter(link => {
          if (link.supervisorOnly && !isSupervisor) return false;
          if (link.supervisorHide && isSupervisor) return false;
          if (link.adminOnly && !isAdmin) return false;
          if (link.adminHide && isAdmin) return false;
          return true;
        }).map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            {label}
          </NavLink>
        ))}

        {isAdmin && (
          <>
            <div className="text-gray-600 text-xs font-semibold uppercase tracking-wider px-3 mt-4 mb-2">Admin</div>
            {ADMIN_LINKS.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {label}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* User footer */}
      <div className="px-3 py-4 border-t border-gray-800">
        <NavLink to="/profile" onClick={() => setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
          <UserCircleIcon className="w-4 h-4" />
          Profile Settings
        </NavLink>
        <button onClick={handleLogout} className="sidebar-link w-full mt-1 text-red-400 hover:text-red-300 hover:bg-red-900/20">
          <ArrowRightOnRectangleIcon className="w-4 h-4" />
          Logout
        </button>
        <div className="mt-3 px-3">
          <div className="text-white text-xs font-medium truncate">{user?.full_name}</div>
          <div className="text-gray-500 text-xs capitalize">{user?.role}</div>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <div className="relative z-10 flex flex-col w-64">
            <Sidebar />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="bg-surface-50 border-b border-gray-800 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <button className="lg:hidden text-gray-400" onClick={() => setSidebarOpen(true)}>
            <Bars3Icon className="w-6 h-6" />
          </button>
          <div className="hidden lg:block text-gray-500 text-sm">
            Health Research Unit Zimbabwe — MSc Fellowship Platform
          </div>
          <div className="flex items-center gap-3">
            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setNotifOpen(!notifOpen)}
                className="relative p-2 text-gray-400 hover:text-white rounded-lg hover:bg-surface-100 transition-all"
              >
                <BellIcon className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-surface-50 border border-gray-700 rounded-xl shadow-2xl z-50">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
                    <span className="font-semibold text-sm">Notifications</span>
                    {unreadCount > 0 && (
                      <button onClick={handleMarkAllRead} className="text-xs text-primary-400 hover:text-primary-300">
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {notifData?.data?.length === 0 ? (
                      <div className="px-4 py-6 text-center text-gray-500 text-sm">All caught up!</div>
                    ) : (
                      notifData?.data?.map((n) => (
                        <div key={n.id} className="px-4 py-3 border-b border-gray-800 hover:bg-surface-100">
                          <div className="text-sm font-medium text-gray-200">{n.title}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{n.message}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <NavLink to="/profile" className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-surface-100 transition-all">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-primary-600 flex items-center justify-center text-white text-xs font-bold">
                  {user?.full_name?.[0]?.toUpperCase()}
                </div>
              )}
              <span className="text-sm text-gray-300 hidden sm:block">{user?.full_name?.split(' ')[0]}</span>
            </NavLink>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
